import { Flow } from '@or-sdk/ccp';
import { watchDebounced } from '@vueuse/core';
import { defineStore, storeToRefs } from 'pinia';
import { ref, computed } from 'vue';

import { Ccp as ccpService } from '@/api';
import config from '@/config';
import { Client, Env } from '@/constants';
import { TranslatableError } from '@/errors';
import {
  GetEnvironmentsDto,
  GetAccountsDto,
  AccountDto,
  CategoryDto,
  MappingDto,
  GetFlowsDto,
  FlowDto,
  EnvironmentDto,
  SkuDto,
} from '@/types';
import { Customer } from '@/types/customer';
import { checkInternalMode, fetchAllPages, filterByUniqueItems } from '@/utils';

import useAuthStore from './auth';

const useCcpStore = defineStore('ccp', () => {
  const authStore = useAuthStore();
  const { role } = storeToRefs(authStore);

  const isEnvironmentsLoading = ref(false);
  const isAccountsLoading = ref(false);

  const customersList = ref<Customer[]>([]);
  const selectedCustomer = ref<string>();

  const skusList = ref<SkuDto[]>([]);
  const environmentsList = ref<EnvironmentDto[]>([]);
  const accountsList = ref<AccountDto[]>([]);
  const categoriesList = ref<CategoryDto[]>([]);
  const flowsList = ref(new Map());
  const mappingList = ref<MappingDto>({});

  const internalMode = computed(() => checkInternalMode(role.value));

  const customAppName = computed(() => {
    if (config.CLIENT === Client.OPS && config.ENV === Env.DEV) return 'CCP DEV';
    return null;
  });

  async function getCustomers() {
    try {
      if (internalMode.value) {
        const customers = await ccpService.customersApi.listOfCustomers();
        customersList.value = customers;
      } else {
        const customer = await ccpService.customersApi.currentCustomer();
        if (!customer) throw new Error('Customer not found');
        customersList.value = [customer];
        selectedCustomer.value = customer.id;
      }
    } catch (e) {
      console.error('Failed to get customer(s)', e);
      throw new TranslatableError('errors.getCustomer');
    }
  }

  async function getEnvironments(params: GetEnvironmentsDto, signal?: AbortSignal) {
    try {
      return await ccpService.environmentsApi.getEnvironments(params, { signal });
    } catch (e) {
      console.error('Failed to get environments(s)', e);
      throw new TranslatableError('errors.getEnvironments');
    }
  }

  async function getAccounts(params: GetAccountsDto, signal?: AbortSignal) {
    try {
      return await ccpService.accountsApi.getAccounts(params, { signal });
    } catch (e) {
      console.error('Failed to get account(s)', e);
      throw new TranslatableError('errors.getAccounts');
    }
  }

  async function getFlows(params: GetFlowsDto): Promise<FlowDto[]> {
    try {
      return await ccpService.flowsApi.getFlows(params);
    } catch (e) {
      console.error('Failed to get flows(s)', e);
      throw new TranslatableError('errors.getFlows');
    }
  }

  function updateFlowsList(payload: FlowDto[]) {
    const newData = payload.reduce((acc, item) => {
      if (!acc[`${item.environmentId}_${item.accountId}`]) acc[`${item.environmentId}_${item.accountId}`] = new Map();
      acc[`${item.environmentId}_${item.accountId}`]?.set(item.flowId, item);
      return acc;
    }, {} as Record<string, Map<string, Flow>>);

    Object.entries(newData).forEach(([k, v]) => flowsList.value.set(k, v));
  }

  async function getCategories() {
    try {
      categoriesList.value = await ccpService.categoriesApi.getCategories();
    } catch (e) {
      console.error('Failed to get categories', e);
      throw new TranslatableError('errors.getCategories');
    }
  }

  async function getSkus() {
    try {
      skusList.value = await fetchAllPages({
        fetchPage: (params) => ccpService.skusApi.getSKUsV2(params),
        initialParams: {
          page: 1,
          limit: 1000,
        },
        getItems: (response) => response.data ?? [],
        getNextParams: ({ response, params, allItems }) => {
          if (allItems.length >= response.total) return null;

          return {
            ...params,
            page: (params.page ?? 1) + 1,
          };
        },
      });
    } catch (e) {
      console.error('Failed to get skus list', e);
      throw new TranslatableError('errors.getSkus');
    }
  }

  async function getMapping() {
    try {
      mappingList.value = await ccpService.mappingApi.getMapping();
    } catch (e) {
      console.error('Failed to get billing mapping', e);
      throw new TranslatableError('errors.getMapping');
    }
  }

  let envController: AbortController;

  watchDebounced(selectedCustomer, async (clientId) => {
    // reset before retrieve
    environmentsList.value = [];

    isEnvironmentsLoading.value = true;

    if (envController) envController.abort();
    envController = new AbortController();
    try {
      if (!clientId) return;
      const results = await getEnvironments({ clientId }, envController.signal);
      environmentsList.value = filterByUniqueItems(results, 'id');
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Fetching clients\' environments failed:', error);
      }
    } finally {
      isEnvironmentsLoading.value = false;
    }
  }, { debounce: 1000 });

  let accountsController: AbortController;

  watchDebounced(selectedCustomer, async (clientId) => {
    // reset before retrieve
    accountsList.value = [];

    isAccountsLoading.value = true;

    if (accountsController) accountsController.abort();
    accountsController = new AbortController();

    try {
      if (!clientId) return;
      accountsList.value = await getAccounts({ clientId }, accountsController.signal);
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Fetching clients\' accounts failed:', error);
      }
    } finally {
      isAccountsLoading.value = false;
    }
  }, { debounce: 1000 });

  return {
    internalMode,
    customAppName,
    isEnvironmentsLoading,
    isAccountsLoading,
    customersList,
    selectedCustomer,
    environmentsList,
    accountsList,
    categoriesList,
    skusList,
    mappingList,
    flowsList,
    getCustomers,
    getEnvironments,
    getAccounts,
    getCategories,
    getSkus,
    getMapping,
    getFlows,
    updateFlowsList,
  };
});

export default useCcpStore;
